import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, AfterViewInit, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { Router } from "@angular/router";
import { Subscription, Observable, of } from "rxjs";
import { tap, map, concatMap } from "rxjs/operators";
import { GridOptions, ColDef, GridApi, RowSelectionOptions, SelectionColumnDef } from "ag-grid-community";
import { AgGridAngular } from "ag-grid-angular";
import { RouterLinkRendererComponent } from "../../../common/components/ag-grid/router-link-renderer.component";
import { OrganizationStub } from "../../../common/models/organization.model";
import { WellService } from '../../../common/services/well.service';
import { WellStub, Well, GISPoint, GISPointTypes } from "../../../common/models/well.model";
import { WellSelectionMode, ComplianceReportingType, ComplianceReportWell, ComplianceReport, ComplianceReportingSearchResult } from "../../models/compliance.model";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { AuthService } from "../../../services/auth.service";
import { LoaderService } from "../../../common/services/loader.service";


@Component({
    selector: 'well-selector',
    templateUrl: './well-selector.component.html',
    styleUrls: ['./well-selector.component.scss'],
    standalone: false
})
export class WellSelectorComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

    @ViewChild(AgGridAngular, { static: true }) grid: AgGridAngular;

    gridOptions: GridOptions;
	gridApi?: GridApi;

    columnAPINumberExternal: ColDef = {
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            openInNewWindow: true,
            textField: "id",
            url: (data: WellStub) => { return `/dashboard/view/well/${data.id}` }
        },
        headerName: "API Number",
        headerTooltip: "API Number",
        field: "id",
        tooltipField: "id",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 130
    } as ColDef;

    columnAPINumber: ColDef = {
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            openInNewWindow: true,
            textField: "id",
            url: (data: WellStub) => { return `/common/organization/${data.organizationId}/well/${data.id}` }
        },
        headerName: "API Number",
        headerTooltip: "API Number",
        field: "id",
        tooltipField: "id",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 130
    } as ColDef;

    columnCounty: ColDef = {
        headerName: "County",
		headerTooltip: "County",
		field: "countyName",
		tooltipField: "countyName",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 135
    } as ColDef;

    columnTownship: ColDef = {
        headerName: "Township",
		headerTooltip: "Township",
		field: "townshipName",
        tooltipField: "townshipName",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    columnWellName: ColDef = {
        headerName: "Well Name",
        headerTooltip: "Well Name",
        valueGetter: this.getWellName(),
        tooltipValueGetter: this.getWellName(),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    columnWellNumber: ColDef = {
        headerName: "Well Number",
        headerTooltip: "Well Number",
        field: "wellNumber",
        tooltipField: "wellNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 140
    } as ColDef;
    modes = WellSelectionMode;

	@Input() enableSelections: boolean = true;

	private _loadedWells: Well[] = [];

    @Input() organization: OrganizationStub;

   

    @Input() savedReport: ComplianceReport;

    selectionColumnDef: SelectionColumnDef = {
            suppressAutoSize: true,
            suppressSizeToFit: true,
            width: 75
        } as SelectionColumnDef;

    private _reportType: ComplianceReportingType;

    @Input() set reportType(value: ComplianceReportingType) {
        this._reportType = value;
    }

    get reportType(): ComplianceReportingType {
        if (this._reportType == null && this.savedReport != null) {
            this._reportType = {
                typeId : this.savedReport.typeId
            } as ComplianceReportingType;
        }
        return this._reportType;
    }

    _reportedWells: ComplianceReportWell[];

    get reportedWells(): ComplianceReportWell[] { return this._reportedWells; }

    @Input() set reportedWells(value: ComplianceReportWell[]) {
        this._selectedWells = value.map(v => v.apiNumber);
        this._reportedWells = value;
       
    }

    private _selectionOptions?: RowSelectionOptions

    get rowSelection(): RowSelectionOptions {
        if (this._selectionOptions == null) {
            let mode = this.selectionMode.toLocaleLowerCase();
            if (mode == 'single') {
                this._selectionOptions = {
                    mode: "singleRow",
                    checkboxes: true,
                    checkboxLocation: "selectionColumn"
                } as RowSelectionOptions;
            } else {
                this._selectionOptions = {
                    mode: "multiRow",
                    checkboxes: true,
                    checkboxLocation: "selectionColumn",
                    headerCheckbox: true,
                    selectAll: 'filtered'
                } as RowSelectionOptions;
            }
        }
        return this._selectionOptions;
    }

    reportTypeChanged: boolean = false;

    searchingWells: boolean = false;

    private _selectedWells: string[] = [];

    @Output() selectedWells: EventEmitter<Well[]> = new EventEmitter<Well[]>();
    get selectionMode(): WellSelectionMode {
        if (this.reportType == null) return WellSelectionMode.None;
        return this.reportType.apiNumbers;
    }
    subscription: Subscription = new Subscription();
    @Output() throw: EventEmitter<any> = new EventEmitter<any>();
	wellList?: ComplianceReportingSearchResult[];
    wheresMyAPIText: string = "Please contact the Division's Permitting Section at 614-265-6922 if the API# does not show up as an available selection.   ​You may be required to submit a data change request and provide supporting documentation to request a data correction of information with the RICS system.";

    constructor(public router: Router, private formatter: GridFormatterService, private wellService: WellService, private auth: AuthService, private _loader: LoaderService) {
    }

    ngOnInit() {        
        this.gridOptions = this.setGridOptions();
        if (this.reportedWells != null && this.reportedWells.length > 0) {
            setTimeout(() => {
                this.findWells();
            }, 500);
        }
    }

    ngAfterViewInit() {
        if (this.gridApi) this.gridApi.sizeColumnsToFit();
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["reportType"] != null) {
            let rptChange = <SimpleChange>changes["reportType"];
            if (rptChange.firstChange == false && rptChange.previousValue != rptChange.currentValue) {
                this.wellList = undefined; //reset wells because report type has changed
                this.reportTypeChanged = true;
            }
        }
    }

    findWells(): void {
        this.searchingWells = true;
        const wellSubs$ = this.searchWells(this.organization).pipe(
			tap((wells: ComplianceReportingSearchResult[]) => { this.wellList = wells; })
        ).subscribe(
            {
                next: (wells: ComplianceReportingSearchResult[]) => {
                    if (this.reportedWells != null && this.reportedWells.length > 0) {
                     setTimeout(() => {
                            if (this.gridApi != null) {
                                this.gridApi.forEachNode((node) => {
                                    node.setSelected(this.reportedWells.find(w => w.apiNumber === node.data.id) != null);
                                });
                            }
                      }, 500);                    
                    }
                },
                error: (error) => {
                    this.searchingWells = false;
                    this.throwError(error);
                }
            });
        this.subscription.add(wellSubs$);
    }

    getWellName() {
        return (obj: any) => {
            if (obj && obj.data) {
				let name: string = obj.data.leaseName;
				if (name == null || name.trim().length == 0) {
					if (obj.data.wellName != null) {
						name = obj.data.wellName.trim();
						if (obj.data.wellNumber != null) {
							let wellNumber: string = obj.data.wellNumber.trim();
							if (name.endsWith(wellNumber)) {
								name = name.substring(0, name.length - wellNumber.length - 1); //remove well number from the end of well name, if present
							}
						}
					}
				}

				if (name == null || name.trim().length == 0) {
					name = "N/A";
				}
				return name;
			}
			return "N/A";
        }
    }


    onGridSelecting(evt: Event): void {
		const selected: ComplianceReportingSearchResult[] = [];
        if (this.gridApi != null) {
            if (this.enableSelections) {
                this._selectedWells = [];
                const rows = this.gridApi.getSelectedRows();
                for (let r = 0; r < rows.length; r++) {
                    selected.push((rows[r] as Well));
                    this._selectedWells.push((rows[r] as Well).id);
                }              
            } else {
                this.gridApi.forEachNode((node: any) => {
                    let isSelected: boolean = this._selectedWells.find(w => w === node.data.id) != null;
                    node.setSelected(isSelected);
                    if (isSelected) selected.push((node.data as Well))
                });
		    }
        }
		let preloadedWells = this._loadedWells.filter(l => selected.find(s => s.id == l.id) != null);
		let unloadedWells = selected.filter(s => this._loadedWells.find(l => l.id == s.id) == null);
		let result: Well[] = [];
		if (preloadedWells.length > 0) {
			result = result.concat(preloadedWells);
		}
		let obs$: Observable<Well[]> = of(result);
		if (unloadedWells.length > 0) {
			obs$ = obs$.pipe(
				concatMap((loaded: Well[]) => {
					return this.wellService.getWells(unloadedWells.map(w => w.id)).pipe(
						map((newLoaded: Well[]) => {
							this._loadedWells = this._loadedWells.concat(newLoaded);
							let allSelected = loaded.concat(newLoaded);
							return allSelected;
						})
					)
				})
			);
		}
		this._loader.toggleLoader(true, "Loading Selected Well(s).  Please wait.", false);
		let subs$: Subscription = obs$.subscribe({
			next: (allSelected: Well[]) => {
				this._loader.toggleLoader(false);
				this.selectedWells.emit(allSelected);
			},
			error: (err) => {
				console.error(err);
				this._loader.toggleLoader(false);
				this.selectedWells.emit([]);
			}
        });
		this.subscription.add(subs$);
    }

    searchWells(organization: OrganizationStub): Observable<WellStub[]> {
        const orgList: string[] = [];
        orgList.push(organization.id);
        if (organization.historicalOwnerNumber != null && organization.historicalOwnerNumber.length > 0) orgList.push(organization.historicalOwnerNumber);
        this.searchingWells = true;
        return this.wellService.getWellsByReportType(orgList, this.reportType.typeId).pipe(tap((wells: Well[]) => { this.searchingWells = false; }));
    }

    setGridOptions(): GridOptions {
        const columns: ColDef[] = [];
        if (this.auth.User.isInternal) {
            columns.push(this.columnAPINumber);
        } else {
            columns.push(this.columnAPINumberExternal);
        }
        columns.push(this.columnWellName);
        columns.push(this.columnWellNumber);
        columns.push(this.columnCounty);
        columns.push(this.columnTownship);
        this.formatter.applyFilterKeyboardHintTooltips(columns);
        return {
            columnDefs: columns,
            defaultColDef: { filter: true, sortable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
			onGridReady: (params) => {
				this.gridApi = params.api;
                this.gridApi.sizeColumnsToFit();
            },
            pagination: true,
            suppressHorizontalScroll: false            
        } as GridOptions;
    }

    throwError(error: any) {
        this.throw.emit(error);
    }
}
